<template>
  <div v-loading="!chartMainData">
    <div v-if="chartMainData" class="chart-template">
      <div class="chart-template__header">
        <div class="chart-template__header-title">{{ chartTitle }}</div>
        <ModelSelect
          v-if="showYearSelect"
          label="Год"
          item-text="value"
          :items="getYearsRange"
          v-model="year"
        />
      </div>
      <div class="chart-template__chart-wrapper">
        <VueApexCharts :height="chartHeight || dynamicHeight" :series="getVisibleItems" :options="getOptions"  type="bar"/>
      </div>
      <div v-if="competence" class="chart-template__legend">
        Уровни компетенций: 
        <span class="chart-template__legend-level">Высокий</span>
        <span class="chart-template__legend-level">Средний</span>
        <span class="chart-template__legend-level">Низкий</span>
      </div>
      <div
        v-if="isHideShowAllBtn"
        class="chart-template__show-all"
        @click="toggleShowAll"
      >
        <span class="chart-template__show-all-text">{{ showAll ? "Свернуть" : "Показать всe" }}</span> 
        <span 
          class="chart-template__show-all-icon"
          :class="{'chart-template__show-all-icon_rotate': showAll }"
        ></span>
      </div>
    </div>
  </div>
</template>
<script>
import VueApexCharts from 'vue-apexcharts';
import ModelSelect from '@/components/UI/models/ModelSelect';
import { CHART_DEFAULT_COLORS, getDefaultBarOptions } from '../chartHelper';

export default {
  name: "BarChartTemplate",
  components: {
    VueApexCharts,
    ModelSelect,
  },
  props: {
    chartTitle: {
      type: String,
    },
    chartMainData: {
      type: Object,
    },
    chartSeriesYKey: {
      type: String,
    },
    chartHeight: {
      type: Number,
    },
    chartOptionsFuncParams: {
      type: Object,
    },
    ageGroups: {
      type: Boolean,
    },
    competence: {
      type: Boolean,
    },
    courses: {
      type: Boolean,
    },
    showYearSelect: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      year: new Date().getFullYear(),
      visibleCount: 10,
      showAll: false,
    };
  },
  watch: {
    year(value) {
      this.$emit('year-update', value);
    },
  },
  methods: {
    getCompetenceFillColor(compentenceLevel) {
      if(compentenceLevel === "advanced") {
        return "#9E7FB1";
      } else if (compentenceLevel === "intermediate") {
        return "#798FC8";
      } else if (compentenceLevel === "basic") {
        return "#BDEBF5";
      }
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
  computed: {
    getOptions() {
      return getDefaultBarOptions(this.chartOptionsFuncParams);
    },
    dynamicHeight() {
      if(this.competence || this.courses) {
        if (this.getVisibleItems[0].data.length) {
          return (this.getVisibleItems[0].data.length * 91) + 75; // высота графика с учетом высоты колонок
        } else {
          return 700;
        }
      } else if(this.ageGroups) {
        return this.getVisibleItems.length * 100;
      } else {
        return 700;
      }
    },
    getCurrentTypeSeries() {
      if(this.ageGroups) {
        return this.chartMainData.ageGroups.map((item, index) => {
          let ageIntervalTitle = "";
          if (!item.ageInterval) {
            ageIntervalTitle = "не указан";
          } else {
            ageIntervalTitle = `${item.ageInterval.from} ${item.ageInterval.to ? '-' : '+' } ${item.ageInterval.to || ''}`;
          }
          return {y: item.total, x: ageIntervalTitle, fillColor: CHART_DEFAULT_COLORS[index]};
        });
      } else if(this.competence) {
        if(!this.chartMainData) return;
        const resultCompetenceSeries = {
          advanced: [],
          intermediate: [],
          basic: [],
        };
        this.chartMainData.competence.forEach((competence, index) => {
          const levelMap = {
              intermediate: false,
              basic: false,
              advanced: false,
          };
          competence.levels.forEach((item, index) => {
            if (item.competenceLevel === "intermediate") {
              resultCompetenceSeries.intermediate.push({
                y: item.totalUsers,
                x: competence.name,
                fillColor: this.getCompetenceFillColor(item.competenceLevel),
              });
              levelMap.intermediate = true;
            }

            if (item.competenceLevel === "basic") {
              resultCompetenceSeries.basic.push({
                y: item.totalUsers,
                x: competence.name,
                fillColor: this.getCompetenceFillColor(item.competenceLevel),
              });
              levelMap.basic = true;
            }

            if (item.competenceLevel === "advanced") {
              resultCompetenceSeries.advanced.push({
                y: item.totalUsers,
                x: competence.name,
                fillColor: this.getCompetenceFillColor(item.competenceLevel),
              });
              levelMap.advanced = true;
            }
          });

          if (!levelMap.intermediate) {
            resultCompetenceSeries.intermediate.push({
              fillColor: this.getCompetenceFillColor('intermediate'),
              x: competence.name,
              y: 0,
            });
          }

          if (!levelMap.basic) {
            resultCompetenceSeries.basic.push({
              fillColor: this.getCompetenceFillColor('basic'),
              x: competence.name,
              y: 0,
            });
          }

          if (!levelMap.advanced) {
            resultCompetenceSeries.advanced.push({
              fillColor: this.getCompetenceFillColor('advanced'),
              x: competence.name,
              y: 0,
            });
          }
        });
        return Object.keys(resultCompetenceSeries).map((item, index) => {
          return {data: resultCompetenceSeries[item]};
        }).sort((a, b) => {
          const order = { 'advanced': 1, 'intermediate': 2, 'basic': 3 };
          return order[a] - order[b];
        });
      } else if(this.courses) {
        if(!this.chartMainData) return;
        const resultCoursesSeries = {
          completed: [],
          started: [],
        };
        this.chartMainData.courses.forEach((course, index) => {
          resultCoursesSeries.completed.push({
            y: course.totalUsersWhoCompleted,
            x: course.name,
            fillColor: CHART_DEFAULT_COLORS[index],
          });
          resultCoursesSeries.started.push({
            y: course.totalUsersWhoStarted,
            x: course.name,
            fillColor: `${CHART_DEFAULT_COLORS[index]}80`,
          });
        });

        return Object.keys(resultCoursesSeries).map((item, index) => {
          return {name: item, data: resultCoursesSeries[item]};
        });
      } else {
        return [];
      }
    },
    isHideShowAllBtn() {
      if(this.competence || this.courses) {
        return this.getCurrentTypeSeries[0].data.length > this.visibleCount && this.getCurrentTypeSeries[0].data.length !== this.visibleCount;
      } else if(this.ageGroups) {
        return this.getCurrentTypeSeries.length > this.visibleCount && this.getCurrentTypeSeries.length !== this.visibleCount;
      } else {
        return null;
      }
    },
    getVisibleItems() {
      let resultSeries = [];
      if (this.ageGroups) {
        return [{
          data: this.getCurrentTypeSeries,
        }];
      }

      if (this.showAll) {
        resultSeries = this.getCurrentTypeSeries;
      } else {
        resultSeries = this.getCurrentTypeSeries.map((item, index) => {
          return {data: item.data.slice(0, this.visibleCount), name: item.name};
        });

      }
      return resultSeries;
    },
    getSeries() {
      if(this.competence) {
        return this.getCurrentTypeSeries;
      } else {
        return [{
          data: this.getCurrentTypeSeries,
        }];
      }
    },
    getYearsRange() {
      const yearsRange = [];
      for (let year = 2017; year <= new Date().getFullYear(); year++ ) {
        yearsRange.unshift({value: year});
      }
      return yearsRange;
    },
  },
}; 
</script>
<style lang="scss">
@import '~@/styles/rem';
@import '~@/styles/chart_styles';
@import '~@/styles/variables';

@include chart-wrapper-box;

.chart-template {
  
  &__legend {
    display: flex;
    margin-left: rem(170);
    gap: rem(24);
    font-family: Montserrat;
    font-size: rem(18);
    line-height: rem(26);
    font-weight: 600;

    &-level {
      display: flex;
      align-items: center;
      
      &:nth-child(1) {
        
        &::before {
          background-color: #9E7FB1;
        }
      }
      &:nth-child(2) {
        
        &::before {
          background-color: #798FC8;
        }
      }
      &:nth-child(3) {
        
        &::before {
          background-color: #BDEBF5;
        }
      }
      &::before {
        content: "";
        border-radius: 50%;
        margin-right: rem(8);
        width: rem(12);
        height: rem(12);
      }
    }
  }
}

.arrow-box {
  background: rgba(255, 255, 255, 1);
  font-size: rem(14);
  line-height: rem(20);
  display: flex;
  flex-direction: column;
  padding: rem(16);
  border-radius: rem(10);

  span:first-child {
    font-weight: 600;
  }
}

.apexcharts-tooltip {
  border: none !important;
  border-radius: rem(10) !important;
}
</style>